import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import React, { createContext, useContext, useEffect, useMemo } from 'react'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { computeDefaultScope } from 'src/hooks/useDashboardFilter'
import {
  DashboardScopeKind,
  IDashboardContext,
  IDashboardScope,
  IQuery,
  IQueryDashboardContextArgs
} from 'src/types/graphql'
import { useDashboardFilterContext } from './DashboardFilterContext'
import { PLATFORM_SCOPE } from './navRules'

export const DASHBOARD_CONTEXT_QUERY = gql`
  query DashboardContext($teamId: String, $hostId: String) {
    dashboardContext(teamId: $teamId, hostId: $hostId) {
      scopes {
        id
        kind
        name
        iconUrl
        segment
        isPartner
        isOnDemand
        isLogistics
        liveListings
        anyListings
        hasLeads
        hasOnPlatformRents
        hasClosedDeals
        inventoryPending
        viewerCanEdit
      }
      renter {
        hasRents
        team {
          id
          name
          iconUrl
        }
      }
    }
  }
`

export type DashboardContextValue = {
  context: IDashboardContext | null
  loading: boolean
  /** The query errored — consumers fall back (Now nav) and never blank. */
  failed: boolean
  /**
   * Scopes narrowed to the filter's current selection. A platform admin on
   * "Todas" gets the synthetic PLATFORM_SCOPE (data is platform-wide).
   */
  scopesInView: IDashboardScope[]
  isPlatformWide: boolean
  refetch: () => void
}

const Ctx = createContext<DashboardContextValue | null>(null)

/**
 * PURE — narrows the server's scopes to the pill selection. 'none' = the
 * viewer's personal scope; a team id = that team; 'all', or an id the server
 * did not return (stale cookie already cleared by useDashboardFilter), = all.
 * With a hostId the server already returned only that host's USER scope.
 */
export function selectScopesInView(
  scopes: IDashboardScope[],
  selectedTeamId: string,
  selectedHostId: string | null
): IDashboardScope[] {
  if (selectedHostId) return scopes
  if (selectedTeamId === 'none') {
    return scopes.filter((s) => s.kind === DashboardScopeKind.User)
  }
  if (selectedTeamId !== 'all') {
    const team = scopes.filter((s) => s.id === selectedTeamId)
    if (team.length) return team
  }
  return scopes
}

/**
 * PURE — "no context and no error" is LOADING, whatever Apollo says: a query
 * skipped during SSR / before the filter is ready reports loading=false, and
 * treating that as "loaded, empty" flashed the Now fallback nav at every
 * off-platform host on a hard load (spec §10).
 */
export function contextStatus(input: {
  hasContext: boolean
  hasError: boolean
}): { loading: boolean; failed: boolean } {
  return {
    loading: !input.hasContext && !input.hasError,
    failed: input.hasError
  }
}

export function DashboardContextProvider({
  children
}: {
  children: React.ReactNode
}) {
  const { isAuthenticated } = useAuthProvider()
  const logger = useLogger()
  const {
    selectedTeamId,
    selectedHostId,
    filterReady,
    isAdmin,
    setComputedDefault
  } = useDashboardFilterContext()
  // Admins are members of ~50 teams; the server refuses to enumerate them, so
  // an admin always asks for ONE target. Non-admins ask once for everything
  // and narrow client-side (no refetch on pill change).
  const isTeamSelected = selectedTeamId !== 'all' && selectedTeamId !== 'none'
  const isPlatformWide = isAdmin && selectedTeamId === 'all' && !selectedHostId
  const variables: IQueryDashboardContextArgs = selectedHostId
    ? { hostId: selectedHostId }
    : isAdmin && isTeamSelected
      ? { teamId: selectedTeamId }
      : {}

  const { data, error, refetch } = useQuery<
    Pick<IQuery, 'dashboardContext'>,
    IQueryDashboardContextArgs
  >(DASHBOARD_CONTEXT_QUERY, {
    variables,
    // Platform-wide admins get the synthetic scope; the ~12 fact reads for
    // the admin's own personal scope would be computed and then ignored.
    skip: !isAuthenticated || !filterReady || isPlatformWide,
    fetchPolicy: 'cache-first'
  })

  // Spec §7 default scope — non-admins only (admins target one scope at a
  // time). State-only: never written to the filter cookie.
  useEffect(() => {
    if (isAdmin || !data?.dashboardContext) return
    const scopes = data.dashboardContext.scopes
    const personal = scopes.find((s) => s.kind === DashboardScopeKind.User)
    setComputedDefault(
      computeDefaultScope({
        teamIds: scopes
          .filter((s) => s.kind === DashboardScopeKind.Team)
          .map((s) => s.id),
        personalAnyListings: personal?.anyListings ?? 0
      })
    )
  }, [data, isAdmin, setComputedDefault])

  useEffect(() => {
    if (error) {
      logger.error('dashboardContext failed', { message: error.message }, error)
    }
  }, [error, logger])

  const value = useMemo<DashboardContextValue>(() => {
    const context = data?.dashboardContext ?? null
    return {
      context,
      ...(isPlatformWide
        ? { loading: false, failed: false }
        : contextStatus({ hasContext: !!context, hasError: !!error })),
      isPlatformWide,
      scopesInView: isPlatformWide
        ? [PLATFORM_SCOPE]
        : context
          ? selectScopesInView(context.scopes, selectedTeamId, selectedHostId)
          : [],
      refetch: () => {
        void refetch()
      }
    }
  }, [data, error, selectedTeamId, selectedHostId, isPlatformWide, refetch])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useDashboardContext(): DashboardContextValue {
  const ctx = useContext(Ctx)
  if (!ctx) {
    throw new Error(
      'useDashboardContext must be used within DashboardContextProvider'
    )
  }
  return ctx
}

/** Null outside the dashboard (ProfileButton on public pages). */
export function useDashboardContextOptional(): DashboardContextValue | null {
  return useContext(Ctx)
}
